import { saveBlob } from 'App/dashboards/_utils/saveBlob'

// Self-contained, client-side downloads for the dashboards (no Highcharts exporting module / export
// server). CSV is built from the rows already on screen; JPG rasterizes the chart's live SVG.

// Neutralize CSV/formula injection: a cell starting with a formula trigger (=, +, -, @, tab, CR)
// gets a leading apostrophe so spreadsheets treat it as text, not a formula.
const FORMULA_LEAD = /^[=+\-@\t\r]/
const sanitizeFormulaLead = (value: string): string => (FORMULA_LEAD.test(value) ? `'${value}` : value)

// Sanitize for formula injection first, then quote only when the (sanitized) field contains a
// comma, quote, or newline (doubling embedded quotes).
const escapeCsvField = (value: string): string => {
  const safe = sanitizeFormulaLead(value)

  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

// Download a CSV from header + row strings. Callers pass exactly the columns/rows shown on screen,
// so the file never contains data the user isn't already seeing.
export const downloadCsv = (filename: string, headers: string[], rows: string[][]) => {
  const lines = [headers, ...rows].map((row) => row.map(escapeCsvField).join(','))
  const csv = lines.join('\r\n')

  saveBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename)
}

// Rasterize a live chart <svg> to a JPG. We serialize the element, draw it onto a white canvas
// (JPG has no transparency), and export at device resolution. Fully client-side.
export const downloadSvgAsJpg = (svg: SVGSVGElement, filename: string) => {
  const { width, height } = svg.getBoundingClientRect()

  if (!width || !height) return

  const clone = svg.cloneNode(true) as SVGSVGElement

  clone.setAttribute('width', String(width))

  clone.setAttribute('height', String(height))

  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')

  const svgString = new XMLSerializer().serializeToString(clone)
  const svgUrl = URL.createObjectURL(new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' }))
  const image = new Image()

  image.onload = () => {
    const scale = window.devicePixelRatio || 2
    const canvas = document.createElement('canvas')

    canvas.width = width * scale

    canvas.height = height * scale

    const ctx = canvas.getContext('2d')

    if (ctx) {
      ctx.scale(scale, scale)

      ctx.fillStyle = '#ffffff'

      ctx.fillRect(0, 0, width, height)

      ctx.drawImage(image, 0, 0, width, height)

      canvas.toBlob((blob) => blob && saveBlob(blob, filename), 'image/jpeg', 0.95)
    }

    URL.revokeObjectURL(svgUrl)
  }

  image.onerror = () => URL.revokeObjectURL(svgUrl)

  image.src = svgUrl
}
